<script lang="ts">
    import { blur } from 'svelte/transition';
    import { onMount } from 'svelte';
    import { _ } from 'svelte-i18n';
    import { inview } from 'svelte-inview/dist/index';
    import { get } from 'svelte/store';
    import * as Sentry from '@sentry/browser';
    import { captureMessage } from '../../utilities/Sentry';

    import { ConnectionApi } from './ConnectionApi';
    import { api, clientSettings, connections_filter_config } from '../../../stores/api';
    import { filter } from '../../../stores/filter';
    import Spinner from '../../shared/Spinner/Spinner.svelte';
    import MessageDanger from '../../shared/Messages/MessageDanger.svelte';
    import { ApiFilterBuilder } from '@automapa/svelte-automapa-template';
    import { onMapReady } from '../../utilities/onMapReady';
    import { JobsOnMap, getJobPin, getJobGroup, getJobLabel, getJobCluster } from './JobsOnMap.js';
    import { RouteOnMap } from './RouteOnMap';
    import MessageFoundItems from '../../shared/MessageFoundItems.svelte';
    import SwitchView from '../../shared/SwitchView.svelte';
    import { debug, routes, simpleView } from '../../../stores/store';
    import ErrorInline from '../../shared/ErrorInline.svelte';
    import { getFilterQuery, resetFilterQuery } from './getFilterQuery';
    import JobOneRow from './RowDesign1/JobOneRow.svelte';
    import JobOneHeader from './RowDesign1/JobOneHeader.svelte';
    import JobListFilter from './JobListFilter.svelte';
    import { Navigation } from '../../utilities/Navigation';
    import { Connection } from './Connection';
    import { BalloonContentGenerator } from '../../shared/BalloonContentGenerator';
    import { RoutesApp } from './Routes/RoutesApp';
    import { RoutesApi } from './Routes/RoutesApi';
    import { getJobColor } from '../../utilities/getJobColor';
    import { Location } from '../Location/Location';
    import { SettingsApi } from '../Settings/SettingsApi';
    import { LocationApi } from '../Location/LocationApi';
    import { getItemsFromLocationTypes } from '../../utilities/getItemsFromLocationTypes';

    export let showMonit: boolean = false;

    let loading: boolean = false;
    let loadingAfterScroll: boolean = false;
    let error: string = '';
    let errorSave: string = '';

    const initialLimit = 20;
    let jobFilter: ApiFilterBuilder = new ApiFilterBuilder(initialLimit);
    let limit: number = initialLimit;
    let offset: number = 0;
    let hasMore = true;

    let expandedIndex: number = -1;
    let lastExpandedJobId: number | null = null;
    let letEditAllFiltered: boolean = false;
    let onlyLowQualityAddresses: boolean = false;
    let selectedTargetDate: string = new Date().toISOString().substring(0, 10);
    let zoomBestFitTimeout: any = null;

    let componentMounted: boolean = false;
    let locationTypesItems: { id: string; label: string }[] = [];

    let ra: RoutesApp = new RoutesApp($api);

    function reloadElements() {
        $routes.selectedOrUnselected = [];
        $routes.selectMode = 'selected';
        if (componentMounted && jobsOnMap) {
            jobMapObjects = {};
            try {
                jobsOnMap.setFilter(getFilterQuery());
                if (zoomBestFitTimeout) {
                    clearTimeout(zoomBestFitTimeout);
                }
                zoomBestFitTimeout = setTimeout(() => {
                    jobsOnMap.zoomBestFit(true);
                }, 250);
            } catch (err) {
                console.error('reloadElements', err);
                resetFilterQuery();
            }
        }

        ra.setFilter(jobFilter);
        ra.setQuery(getFilterQuery());

        Promise.all([
            ra.getItems(),
            ra.getCurrentTransactionCount()
        ])
            .then(response => {
                if (routeOnMap) {
                    routeOnMap.removeEventListener('routechange', onRouteChange);
                    routeOnMap.destroy();
                    routeOnMap = new RouteOnMap();
                    routeOnMap.addEventListener('routechange', onRouteChange);
                }
                $routes.filteredJobs = response[0];
                $routes.currentTransactionCount = response[1];
                hasMore = response[0].length !== 0;
                offset = 0;
                expandedIndex = -1;
                letEditAllFiltered = Object.keys(getFilterQuery()).length > 0 && $routes.filteredJobs.length > 0 && !hasMore;
            })
            .catch(err => {
                error = err;
                resetFilterQuery();
            });

        Promise.all([ra.countFilteredItems(), ra.countAllItems()])
            .then(response => {
                $routes.countFiltered = response[0];
                $routes.countTotal = response[1];
            })
            .catch(err => {
                console.error('reloadElements - cannot count items', err);
                $routes.countFiltered = { count: 0 };
                $routes.countTotal = { count: 0 };
            });
    }

    function onFilterChange() {
        if (offset + limit > $routes.filteredJobs.length) {
            offset = 0;
        }
        $routes.selectedOrUnselected = [];
        $routes.selectMode = 'selected';
        jobFilter = jobFilter;
        expandedIndex = -1;
        reloadElements();
    }

    function onRouteChange(evt: any) {
        // TODO: call modified openJob function ( with added replaceIfExists arg)
        // openJob(evt.detail.connection_id, true);
        RoutesApi.query($api, { 'connection.id': evt.detail.connection_id }, new ApiFilterBuilder(1))
            .then(r => {
                for (let i = 0; i < $routes.filteredJobs.length; i++) {
                    if ($routes.filteredJobs[i].connection.id == r[0].connection.id) {
                        $routes.filteredJobs[i] = r[0];
                    }
                }
                setTimeout(() => {
                    scrollToJob(evt.detail.connection_id);
                }, 50);
            })
            .catch(err => {
                error = err;
                console.error('JobList.openJob', err);
            });
        //reloadElements(false);
        //console.log('onRouteChange', evt);
        //openJob(evt.detail.connection_id);
    }

    // Will be called when we've scrolled to the pont where more data needs to be fetched
    const handleChange = (e: any) => {
        console.warn('handleChange is called');
        // return false; // FIXME: temporary
        if (e.detail.inView && hasMore) {
            loadingAfterScroll = true;
            offset = offset + limit;
            jobFilter.paginate(limit, 0);
            let tempJobFilter = Object.assign(Object.create(Object.getPrototypeOf(jobFilter)), jobFilter);
            // $api, getFilterQuery(), tempJobFilter.paginate(limit, offset)
            ra.setQuery(getFilterQuery());
            ra.setFilter(tempJobFilter.paginate(limit, offset));
            ra.getItems()
                .then(response => {
                    $routes.filteredJobs = [...$routes.filteredJobs];
                    response.forEach(job => {
                        if (!$routes.filteredJobs.find(filteredJob => filteredJob.connection.id === job.connection.id)) {
                            $routes.filteredJobs.push(job);
                        }
                    });
                    hasMore = response.length !== 0;
                    letEditAllFiltered = Object.keys(getFilterQuery()).length > 0 && $routes.filteredJobs.length > 0 && !hasMore;
                })
                .finally(() => {
                    loadingAfterScroll = false;
                });
        }
    };

    let onJobCoordsSet = function (id: number | null | undefined) {
        if (typeof id == 'number') {
            delete jobMapObjects[id];
            jobsOnMap.jobReloadSingle(id);
        } else {
            jobMapObjects = {};
            jobsOnMap.jobsReload();
        }
    };

    // let clickedJob: any;
    let jobsOnMap: JobsOnMap;
    let routeOnMap: RouteOnMap;
    let jobMapObjects: any = {};
    let today = (function () {
        let d = new Date();
        return d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2);
    })();

    const getJobMapObject = (job: any) => {
        let mapObj;
        let zIndex = 502 + 90 - job.geom.coordinates[1]; // Make icons place like for real
        if (job.count && job.count > 1) {
            mapObj = new Targeo.MapObject.Point(
                { x: job.geom.coordinates[0], y: job.geom.coordinates[1] },
                getJobGroup('#ffffff', 30 + (job.count.toString().length - 1) * 10, {
                    total: job.count,
                    filtered: job.filter,
                })
            );
        } else {
            let coordkey = JSON.stringify(job.geom.coordinates);
            if (!(job.id in jobMapObjects) || jobMapObjects[job.id].coords != coordkey) {
                let color = getJobColor(job);
                mapObj = new Targeo.MapObject.Point(
                    { x: job.geom.coordinates[0], y: job.geom.coordinates[1] },
                    getJobPin(
                        color,
                        job.filter === false
                            ? JobsOnMap.pinSizes.unmatching
                            : job.filter === true
                              ? JobsOnMap.pinSizes.matching
                              : JobsOnMap.pinSizes.normal,
                        { type: job.doc },
                        $clientSettings.display.icon_style
                    ),
                    'job_' + job.id,
                    {
                        name: getJobLabel(job),
                        cluster: getJobCluster(
                            color,
                            job.filter === false
                                ? JobsOnMap.pinSizes.unmatching
                                : job.filter === true
                                  ? JobsOnMap.pinSizes.matching
                                  : JobsOnMap.pinSizes.normal,
                            { type: job.doc },
                            $clientSettings.display.icon_style
                        ),
                        zIndex: zIndex,
                        balloon: {
                            body: '<div class="lds-dual-ring-improved" style="width: 60px; height: 60px;"></div>',
                        },
                        mouseButtonActions: {
                            left: function (mapObj: any) {
                                let jobId = parseInt(mapObj.id.slice(4));
                                console.log('JobList, point on map clicked', mapObj.id, jobId);
                                if (expandedIndex === jobId) {
                                    closeJob(jobId);
                                } else {
                                    openJob(jobId);
                                }
                            },
                        },
                    }
                );
                jobMapObjects[job.id] = {
                    coords: coordkey,
                    mapObject: mapObj,
                    detailsLoaded: false,
                    balloonListener: null,
                    color: color,
                    pinOpts: { type: job.doc },
                };
            } else {
                mapObj = jobMapObjects[job.id].mapObject;
            }
            if (!jobMapObjects[job.id].detailsLoaded) {
                if (jobMapObjects[job.id].balloonListener) {
                    jobMapObjects[job.id].mapObject.unlistenByKey(jobMapObjects[job.id].balloonListener);
                }
                jobMapObjects[job.id].balloonListener = mapObj.listenOnce('onBalloonShow', function (ev: any) {
                    jobMapObjects[job.id].balloonListener = null;
                    var jobId = parseInt(ev.target.id.slice(4));
                    ConnectionApi.getOne($api, jobId)
                        .then(job => {
                            let html = new BalloonContentGenerator(new Location().set(job)).getBalloonHtmlArray();
                            jobMapObjects[job.id].mapObject.setBalloonOptions({
                                body: html.join(''),
                            });
                            Targeo.Base.Map.getObjectManager().refreshBalloon('job_' + job.id);
                            jobMapObjects[job.id].detailsLoaded = true;
                        })
                        .catch(err => {
                            error = err;
                            resetFilterQuery();
                        });
                });
            }
        }
        return mapObj;
    };

    const emphasiseJobOnMap = (jobId: number, emphasise: boolean) => {
        if (jobMapObjects[jobId] && jobMapObjects[jobId].mapObject) {
            jobMapObjects[jobId].mapObject.setIcon(
                getJobPin(
                    jobMapObjects[jobId].color,
                    emphasise ? JobsOnMap.pinSizes.matching : JobsOnMap.pinSizes.normal,
                    jobMapObjects[jobId].pinOpts,
                    $clientSettings.display.icon_style
                )
            );
        }
    };

    function openJob(jobId: number) {
        if (lastExpandedJobId !== null) {
            emphasiseJobOnMap(lastExpandedJobId, false);
        }
        lastExpandedJobId = jobId;
        emphasiseJobOnMap(jobId, true);
        if (!$routes.filteredJobs.find(r => r.connection.id === jobId)) {
            //. load job in not loaded in the list (dynamically)
            RoutesApi.getOne($api, jobId)
                .then(r => {
                    $routes.filteredJobs = [...$routes.filteredJobs, r];
                    expandedIndex = jobId; // toggle job to open after loaded
                    // scrollToJob(jobId);
                    setTimeout(() => {
                        scrollToJob(jobId);
                    }, 50);
                })
                .catch(err => {
                    error = err;
                    console.error('JobList.openJob', err);
                });
        } else {
            expandedIndex = jobId; // toggle job to open if already loaded
            setTimeout(() => {
                scrollToJob(jobId);
            }, 50);
        }
    }

    function closeJob(jobId: number) {
        expandedIndex = -1;
        emphasiseJobOnMap(jobId, false);
    }

    function scrollToJob(jobId: number) {
        const jobElement = document.getElementById(`job-${jobId}`);
        if (jobElement) {
            jobElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    onMount(() => {
        componentMounted = true;
        Promise.all([
            onMapReady(),
            LocationApi.getLocationTypes(get(api)),
            // SettingsApi.getFilterConfig($api, 'view_connection'),
        ])
            .then(response => {
                locationTypesItems = getItemsFromLocationTypes(response[1]);
                // $connections_filter_config = response[2];
                if (componentMounted) {
                    /*jobsOnMap = new JobsOnMap({
                        getMapObject: getJobMapObject,
                        projection: ['id', 'geom'],
                        filter: getFilterQuery(),
                        maxPointZoom: 32,
                        addJob: true,
                    });
                    jobsOnMap.addEventListener('jobadd', (evt: any) => {
                        console.log('!!! addJob event', evt.detail);
                        $routes.revGeoDetails = evt.detail;
                        Navigation.navigate('/manual-add-job/');
                    });
                    jobsOnMap.zoomBestFit(true);*/
                    routeOnMap = new RouteOnMap();
                    routeOnMap.addEventListener('routechange', onRouteChange);
                }
            })
            .catch(err => {
                error = err;
                resetFilterQuery();
            });

        return () => {
            componentMounted = false;
            if (routeOnMap) {
                routeOnMap.removeEventListener('routechange', onRouteChange);
                routeOnMap.destroy();
            }
            if (jobsOnMap) {
                jobMapObjects = {};
                jobsOnMap.destroy();
            } else {
                // captureMessage('captureMessage: JobsList.onMount: jobsOnMap not ready');
            }
        };
    });

    $: {
        if (!error) {
            $filter.connections.queries = $filter.connections.queries;
            reloadElements();
        }
    }

    $: {
        if ($routes.forceRecount) {
            $routes.forceRecount = false;
            $routes.selectedOrUnselected = [];
            $routes.selectMode = 'selected';
            Promise.all([
                RoutesApi.getCountWithQueries($api, getFilterQuery(), jobFilter),
                RoutesApi.getCountWithQueries($api, {}, jobFilter),
            ])
                .then(response => {
                    $routes.countFiltered = response[0];
                    $routes.countTotal = response[1];
                })
                .catch(err => {
                    error = err;
                    resetFilterQuery();
                });
        }
    }

    $: {
        if ($routes.forceReload) {
            $routes.forceReload = false;
            $routes.selectedOrUnselected = [];
            $routes.selectMode = 'selected';
            reloadElements();
            if (error) {
                resetFilterQuery();
            }
        }
    }

    // $: {
    //     if (error) {
    //         resetFilterQuery();
    //     }
    // }

    // $: {
    //     expandedIndex = expandedIndex;
    //     $routes = $routes;
    //     console.log('JobList, expandedIndex', expandedIndex);
    // }
</script>

{#if error}
    <MessageDanger message={$_('Failed to fetch routes')} bind:error />
{/if}
{#if errorSave}
    <ErrorInline text={errorSave} />
{/if}

<div style="margin-top: 10px;" class:blur={showMonit}>
    <JobListFilter makeBlured={false} filteredCount={$routes.countFiltered.count} totalCount={$routes.countTotal.count} />
</div>
{#if loading}
    <div in:blur={{ delay: 500, duration: 1000 }}>
        <Spinner />
    </div>
{:else}
    <!--{#if $routes.countTotal.count > 0}-->
    <!--    <div style="flex:0; justify-content: flex-end; display: flex;">-->
    <!--        <span style="margin-left: auto;">-->
    <!--            <SwitchView bind:checked={$simpleView} />-->
    <!--        </span>-->
    <!--    </div>-->
    <!--{/if}-->

    {#if $debug}
        {JSON.stringify(jobFilter.getSortBy())}
        expandedIndex={expandedIndex}
    {/if}

    {#if $routes.countTotal.count > 0}
        <JobOneHeader bind:jobFilter {onFilterChange} bind:simpleView={$simpleView} />
    {/if}

    <div class="job-list">
        {#each $routes.filteredJobs as route, index (route.unique_id)}
            <div in:blur|global id={`job-${route.connection.id}`} style="padding-right: 10px;">
                <JobOneRow
                    bind:route
                    index={route.connection.id}
                    bind:expandedIndex
                    showHeader={false}
                    {jobsOnMap}
                    {routeOnMap}
                    bind:simpleView={$simpleView}
                    on:setcoordinates={() => onJobCoordsSet(route.connection.id)}
                    on:open={() => {
                        console.log('JobOneRow on:open');
                        openJob(route.connection.id);
                    }}
                    on:close={() => {
                        console.log('JobOneRow on:close');
                        closeJob(route.connection.id);
                    }}
                    {locationTypesItems}
                />
            </div>
        {/each}

        {#if loadingAfterScroll}
            <Spinner />
        {:else if hasMore}
            <div
                class="inview-element"
                use:inview={{}}
                on:change={e => {
                    handleChange(e);
                }}
            />
        {:else}
            <MessageFoundItems count={$routes.filteredJobs.length} />
        {/if}
    </div>
{/if}

<style>
    .inview-element {
        height: 100px;
    }
    .job-list {
        display: flex;
        flex-direction: column;
        overflow-y: auto;
        overflow-x: hidden;
        padding-bottom: 40px;
        margin-bottom: 20px;
        width: 100%;
    }
</style>
